import Vue from "vue"
import Router from "vue-router"
import multiguard from "vue-router-multiguard"
import { ifAuthenticated, ifNotAuthenticated, hasPermissions, hasRoles } from "./guards"

Vue.use(Router)

const routes = [
  {
    path: "/",
    redirect: "/documents-overview"
  },
  {
    path: "/documents-overview",
    name: "Documents Overview",
    meta: {
      title: "Pregled dokumentov",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/DocumentsOverview"])])
    },
    component: () => import(/* webpackChunkName: "Overview" */ "@/views/DocumentsOverview/List.vue")
  },
  {
    path: "/board",
    name: "Board",
    meta: {
      title: "Tabla",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Board"])])
    },
    component: () => import(/* webpackChunkName: "Overview" */ "@/views/Board/View.vue")
  },
  {
    path: "/login",
    name: "Login",
    meta: {
      title: "Prijava",
      beforeEnter: ifNotAuthenticated
    },
    component: () => import(/* webpackChunkName: "Login" */ "@/views/Login/Login.vue")

  },
  {
    path: "/login/forgot-password",
    name: "Forgot password",
    meta: {
      title: "Ponastavitev gesla",
      beforeEnter: ifNotAuthenticated
    },
    component: () => import(/* webpackChunkName: "Login" */ "@/views/Login/ForgotPassword.vue")
  },
  {
    path: "/settings",
    name: "settings",
    meta: {
      title: "Nastavitve",
      beforeEnter: ifAuthenticated
    },
    component: () => import(/* webpackChunkName: "Settings" */ "@/views/Settings/Details.vue")
  },
  {
    path: "/companies",
    name: "Companies",
    meta: {
      title: "Podjetja",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Companies"])])
    },
    component: () => import(/* webpackChunkName: "Companies" */ "@/views/Companies/List.vue")
  },
  {
    path: "/companies/details/:id",
    name: "Company details",
    meta: {
      title: "Pregled podjetja",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Companies"])])
    },
    component: () => import(/* webpackChunkName: "Companies" */ "@/views/Companies/Details.vue")
  },
  {
    path: "/companies/edit/:id",
    name: "Company edit",
    meta: {
      title: "Urejanje podjetja",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Companies"])])
    },
    component: () => import(/* webpackChunkName: "Companies" */ "@/views/Companies/Edit.vue")
  },
  {
    path: "/companies/new",
    name: "Company new",
    meta: {
      title: "Dodajanje podjetja",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Companies"])])
    },
    component: () => import(/* webpackChunkName: "Companies" */ "@/views/Companies/Edit.vue")
  },
  {
    path: "/employees",
    name: "Employees",
    meta: {
      title: "Zaposleni",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Employees"])])
    },
    component: () => import(/* webpackChunkName: "Employees" */ "@/views/Employees/List.vue")
  },
  {
    path: "/employees/details/:id",
    name: "Employee Details",
    meta: {
      title: "Oseba",
      field: "full_name",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Employees"])])
    },
    component: () => import(/* webpackChunkName: "Employees" */ "@/views/Employees/Details.vue")
  },
  {
    path: "/employees/edit/:id",
    name: "Edit Employee",
    meta: {
      title: "Urejanje osebe",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Employees"])])
    },
    component: () => import(/* webpackChunkName: "Employees" */ "@/views/Employees/Edit.vue")
  },
  {
    path: "/employees/new",
    name: "New Employee",
    meta: {
      title: "Nova oseba",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Employees"])])
    },
    component: () => import(/* webpackChunkName: "Employees" */ "@/views/Employees/Edit.vue")
  },
  {
    path: "/projects",
    name: "Projects",
    meta: {
      title: "Projekti",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Projects"])])
    },
    component: () => import(/* webpackChunkName: "Projects" */ "@/views/Projects/List.vue")
  },
  {
    path: "/projects/:id",
    name: "Project Details",
    meta: {
      title: "Projekt",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Projects"])])
    },
    component: () => import(/* webpackChunkName: "Projects" */ "@/views/Projects/Details.vue")
  },
  {
    path: "/user-management",
    name: "User management list",
    meta: {
      title: "Nadzor nad uporabniki",
      guard: "User",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/User"])])
    },
    component: () => import(/* webpackChunkName: "UserManagement" */ "@/views/UserManagement/List.vue")
  },
  {
    path: "/user-management/:id",
    name: "User management",
    meta: {
      title: "Nadzor nad uporabnikom",
      guard: "User",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/User"])])
    },
    component: () => import(/* webpackChunkName: "UserManagement" */ "@/views/UserManagement/Details.vue")
  },
  {
    path: "/codelists",
    name: "codeLists",
    meta: {
      title: "Šifranti",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Codelist"])])
    },
    component: () => import(/* webpackChunkName: "Codelists" */ "@/views/Codelists/List.vue")
  },
  {
    path: "/document-templates",
    name: "documentTemplates",
    meta: {
      title: "Predloge dokumentov",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/DocumentTemplate"])])
    },
    component: () => import(/* webpackChunkName: "DocumentTemplates" */ "@/views/DocumentTemplates/List.vue")
  },
  {
    path: "/cars-overview",
    name: "Cars Overview",
    meta: {
      title: "Pregled potekov - avtomobili",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Cars"])])
    },
    component: () => import(/* webpackChunkName: "Cars" */ "@/views/CarsOverview/List.vue")
  },
  {
    path: "/cars",
    name: "cars",
    meta: {
      title: "Avtomobili",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Cars"])])
    },
    component: () => import(/* webpackChunkName: "Cars" */ "@/views/Cars/List.vue")
  },
  {
    path: "/cars/details/:id",
    name: "Car details",
    meta: {
      title: "Pregled avta",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Cars"])])
    },
    component: () => import(/* webpackChunkName: "Cars" */ "@/views/Cars/Details.vue")
  },
  {
    path: "/apartments",
    name: "apartments",
    meta: {
      title: "Stanovanja",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Apartments"])])
    },
    component: () => import(/* webpackChunkName: "Apartments" */ "@/views/Apartments/List.vue")
  },
  {
    path: "/apartments/details/:id",
    name: "Apartment details",
    meta: {
      title: "Pregled stanovanja",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Apartments"])])
    },
    component: () => import(/* webpackChunkName: "Apartments" */ "@/views/Apartments/Details.vue")
  },
  {
    path: "/lists",
    name: "lists",
    meta: {
      title: "Seznami",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Lists"])])
    },
    component: () => import(/* webpackChunkName: "Administration" */ "@/views/Lists/index.vue")
  },
  {
    path: "/lists/registered-on-day",
    name: "listRegisteredOnDay",
    meta: {
      title: "Seznam zaposlenih na dan",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Lists"])])
    },
    component: () => import(/* webpackChunkName: "Administration" */ "@/views/Lists/ListViews/RegisteredOnDay/List.vue")
  },
  {
    path: "/lists/a1",
    name: "listA1",
    meta: {
      title: "Seznam A1",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Lists"])])
    },
    component: () => import(/* webpackChunkName: "Administration" */ "@/views/Lists/ListViews/A1/List.vue")
  },
  {
    path: "/lists/ovo",
    name: "listOvo",
    meta: {
      title: "Seznam OVO",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Lists"])])
    },
    component: () => import(/* webpackChunkName: "Administration" */ "@/views/Lists/ListViews/OVO/List.vue")
  },
  {
    path: "/lists/health-and-safety",
    name: "listHealthAndSafety",
    meta: {
      title: "Seznam varstvo pri delu",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Lists"])])
    },
    component: () => import(/* webpackChunkName: "Administration" */ "@/views/Lists/ListViews/HealthAndSafety/List.vue")
  },
  {
    path: "/lists/medical-ids",
    name: "listMedicalIds",
    meta: {
      title: "Seznam zdravstvene kartice",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Lists"])])
    },
    component: () => import(/* webpackChunkName: "Administration" */ "@/views/Lists/ListViews/MedicalIds/List.vue")
  },
  {
    path: "/lists/certificates",
    name: "listCertificates",
    meta: {
      title: "Seznam certifikatov",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Lists"])])
    },
    component: () => import(/* webpackChunkName: "Administration" */ "@/views/Lists/ListViews/Certificates/List.vue")
  },
  {
    path: "/lists/risk-assessments",
    name: "listRiskAssessments",
    meta: {
      title: "Seznam ocen tveganja",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Lists"])])
    },
    component: () => import(/* webpackChunkName: "Administration" */ "@/views/Lists/ListViews/RiskAssessments/List.vue")
  },
  {
    path: "/lists/medical-certificates",
    name: "listMedicalCertificates",
    meta: {
      title: "Seznam zdravniška spričevala",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Lists"])])
    },
    component: () => import(/* webpackChunkName: "Administration" */ "@/views/Lists/ListViews/MedicalCertificates/List.vue")
  },
  {
    path: "/lists/insurance/:type",
    name: "listMedicalCertificates",
    meta: {
      title: "Seznam kolektivna zavarovanja",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Lists"])])
    },
    component: () => import(/* webpackChunkName: "Administration" */ "@/views/Lists/ListViews/Insurances/List.vue")
  },
  {
    path: "/lists/cars",
    name: "listCarLeases",
    meta: {
      title: "Najemi avtomobilov",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Lists"])])
    },
    component: () => import(/* webpackChunkName: "Administration" */ "@/views/Lists/ListViews/CarLeases/List.vue")
  },
  {
    path: "/lists/apartments",
    name: "listApartmentLeases",
    meta: {
      title: "Najemi stanovanj",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Lists"])])
    },
    component: () => import(/* webpackChunkName: "Administration" */ "@/views/Lists/ListViews/ApartmentLeases/List.vue")
  },
  {
    path: "/lists/attendance",
    name: "attendanceTable",
    meta: {
      title: "Evidenca prisotnosti",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Lists"])])
    },
    component: () => import(/* webpackChunkName: "Administration" */ "@/views/Attendances/List.vue")
  },
  {
    path: "/td-review",
    name: "TD Review",
    meta: {
      title: "Pregled tehničnega direktorja",
      beforeEnter: multiguard([ifAuthenticated, hasPermissions(["view/Projects"]), hasRoles(["technical-director", "administrator"])])
    },
    component: () => import(/* webpackChunkName: "TdReview" */ "@/views/TdReview/List.vue")
  },
  {
    path: "*",
    redirect: "/settings"
  }
]

const router = new Router({
  // mode: "history",
  base: process.env.BASE_URL,
  routes
})

router.beforeEach((to, from, next) => {
  if (!to.meta.beforeEnter) return next()
  to.meta.beforeEnter(to, from, next)
})

export default router
